import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { BackendService } from '@services';
import { map } from 'rxjs/operators';
import { mpiConfig } from '@app/core/config/app-config.constants';
import { ICreateUserData, IUser } from '@app/core/data-models/user/user.model';
import { UserBulkAction } from '@app/core/data-models/types/user.type';
import { IUserDetailsData, IUserSettingsData } from '@app/store/users.store';
import { ApiResponseSuccessInterface } from '../interfaces/api-response-success';
import { LoginHistoryResponse } from '@app/containers/mci/login-history/login-history.config';
import { UserChangeLogResponse } from '@app/containers/mci/user-log/user-log.config';
import { IActivityV4Response, IActivityV4Request } from '@app/containers/mci/activity-log/activity-log.config';

@Injectable({
    providedIn: 'root',
})
export class UsersService extends BackendService {
    /**
     * Load users
     * @returns Observable<IUser[]>
     */
    loadUsers(): Observable<IUser[]> {
        return this.doGet(mpiConfig.api.loadUsers).pipe(map((response) => response.data?.users || []));
    }

    /**
     * Create a new user
     * @param data - User creation data
     * @returns Observable<IUser>
     */
    createUser(data: ICreateUserData): Observable<IUser> {
        return this.doPost(mpiConfig.api.addUser, JSON.stringify(data)).pipe(map((response) => response.data));
    }

    /**
     * Update user details
     * @param userName - The username of the user to update
     * @param userDetails - The user details to update
     * @returns Observable<IUser>
     */
    updateUserDetails(userName: string, userDetails: IUserDetailsData): Observable<IUser> {
        return this.doPut(mpiConfig.api.updateUserDetails(userName), JSON.stringify(userDetails)).pipe(
            map((response) => response.data),
        );
    }

    /**
     * Update user company details
     * @param userName - The username of the user to update
     * @param userSettings - The user company settings to update
     * @returns Observable<IUser>
     */
    updateUserCompanyDetails(userName: string, userSettings: IUserSettingsData): Observable<IUser> {
        return this.doPut(mpiConfig.api.updateUserCompanyDetails(userName), JSON.stringify(userSettings)).pipe(
            map((response) => response.data),
        );
    }

    /**
     * Send login email to user
     * @param userName - The username of the user to send login email to
     * @returns Observable<ApiResponseSuccessInterface>
     */
    sendUserLoginEmail(userName: string): Observable<ApiResponseSuccessInterface> {
        return this.doPost(mpiConfig.api.sendUserLoginEmail(userName), '');
    }

    /**
     * Perform bulk actions on users
     * @param action - The action to perform: 'enabled' | 'disabled' | 'emailed' | 'unlocked'
     * @param users - Array of user names
     * @returns Observable<any>
     */
    bulkAction(action: UserBulkAction, users: string[]): Observable<any> {
        const payload = { action, users };
        return this.doPut(mpiConfig.api.userBulkAction, JSON.stringify(payload));
    }

    /**
     * Get user login history
     * @param userName - The username to get login history for
     * @returns Observable<LoginHistoryResponse>
     */
    getUserLoginHistory(userName: string): Observable<LoginHistoryResponse> {
        return this.doGet(mpiConfig.api.getUserLoginHistory(userName));
    }

    /**
     * Get user change log for detailed activity monitoring
     * @param userName - The username to get change log for
     * @returns Observable<UserChangeLogResponse>
     */
    getUserChangeLog(userName: string): Observable<UserChangeLogResponse> {
        return this.doGet(mpiConfig.api.getUserChangeLog(userName));
    }

    /**
     * Get user activity log with pagination
     * Calls our backend which will proxy to external API
     * 
     * @param userId - User ID to filter activities
     * @param startDate - Start date filter (format: YYYY-MM-DD)
     * @param endDate - End date filter (format: YYYY-MM-DD)
     * @param page - Page number (default: 0)
     * @param size - Page size (default: 25)
     * @returns Observable<IActivityV4Response>
     */
    getUserActivity(
        userId: string,
        startDate: string,
        endDate: string,
        page: number = 0,
        size: number = 25
    ): Observable<IActivityV4Response> {
        const requestBody: IActivityV4Request = {
            userId,
            startDate,
            endDate,
            page,
            size
        };

        return this.doPost(mpiConfig.api.getUserActivity, JSON.stringify(requestBody));
    }

    /**
     * Get all user activity log records (fetches all pages from external API)
     * Use this for client-side sorting when all data is needed
     * 
     * @param userId - User ID to filter activities
     * @param startDate - Start date filter (format: YYYY-MM-DD)
     * @param endDate - End date filter (format: YYYY-MM-DD)
     * @returns Observable<IActivityV4Response>
     */
    getAllUserActivity(
        userId: string,
        startDate: string,
        endDate: string
    ): Observable<IActivityV4Response> {
        const requestBody = {
            userId,
            startDate,
            endDate,
            page: 0,
            size: 10000
        };

        return this.doPost(mpiConfig.api.getAllUserActivity, JSON.stringify(requestBody));
    }

    /**
     * Export user activity log to Excel or CSV file
     * Calls backend which generates file using ActivityLogExport
     * 
     * @param userId - User ID to export activities for
     * @param startDate - Start date filter (format: YYYY-MM-DD)
     * @param endDate - End date filter (format: YYYY-MM-DD)
     * @param format - Export format: 'xlsx' or 'csv' (default: 'xlsx')
     * @returns Observable<Blob>
     */
    exportUserActivity(
        userId: string,
        startDate: string,
        endDate: string,
        format: 'xlsx' | 'csv' = 'xlsx'
    ): Observable<Blob> {
        const requestBody = {
            userId,
            startDate,
            endDate,
            page: 0,
            size: 10000,
            format
        };

        return this.doPostBlob(mpiConfig.api.exportUserActivity, JSON.stringify(requestBody)).pipe(
            map((response) => response.body)
        );
    }
}